// gsapAnimateNumbers.js
import { gsap, ScrollTrigger } from "./gsapSetup";

function isMeasurable(el) {
  if (!el || !el.isConnected) return false;
  const cs = getComputedStyle(el);
  if (cs.display === "none") return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

function whenMeasurable(el, cb) {
  let tries = 0;

  const tick = () => {
    tries += 1;
    if (isMeasurable(el)) return cb();
    if (tries < 20) return requestAnimationFrame(tick);

    const io = new IntersectionObserver(() => {
      if (isMeasurable(el)) {
        io.disconnect();
        cb();
      }
    });
    io.observe(el);
  };

  requestAnimationFrame(tick);
}

/**
 * Build a vertical "odometer" track 0..9 repeated enough times.
 * Returns totalSteps (max index in the track).
 */
function buildDigitTrack(trackEl, steps, leadCycles = 1) {
  // leadCycles adds extra 0-9 runs so "fromTop" has room for a non-final start
  const totalSteps = leadCycles * 10 + steps;

  for (let i = 0; i <= totalSteps; i++) {
    const n = document.createElement("span");
    n.className = "n";
    n.textContent = String(i % 10);
    trackEl.appendChild(n);
  }

  return totalSteps;
}

/**
 * Odometer-like per-digit counter that triggers on scroll.
 * - Supports decimals (e.g. 4.8)
 * - Alternating direction per digit:
 *    even index digits roll "from top" (track moves DOWN)
 *    odd index digits roll "from bottom" (track moves UP)
 * - Staggered finish: left digits finish faster than right
 */
export function initGsapDigitCountersOnScroll({
                                                selector = ".gs-percentage",
                                                duration = 2,
                                                staggerExtra = 0.8,
                                                loops = 2,
                                                leadCycles = 1,
                                                ease = "power2.out",
                                                start = "top 80%",
                                                once = true,
                                              } = {}) {
  const els = document.querySelectorAll(selector);
  if (!els.length) return;

  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    els.forEach((el) => (el.textContent = (el.dataset.target ?? "").toString()));
    return;
  }

  els.forEach((el) => {
    const targetRaw = (el.dataset.target ?? "").toString().trim();
    if (!targetRaw) return;

    const isNegative = targetRaw.startsWith("-");
    const target = isNegative ? targetRaw.slice(1) : targetRaw;

    // Build DOM
    el.innerHTML = "";
    el.classList.add("counter");
    if (!el.style.lineHeight) el.style.lineHeight = "1";

    if (isNegative) {
      const minus = document.createElement("span");
      minus.className = "sep";
      minus.textContent = "−";
      el.appendChild(minus);
    }

    const chars = target.split("");
    const digits = []; // { trackEl, totalSteps, finalDigit, idx, fromTop, startY, endY }

    let digitIndex = 0;

    chars.forEach((ch) => {
      if (!/\d/.test(ch)) {
        const sep = document.createElement("span");
        sep.className = "sep";
        sep.textContent = ch;
        el.appendChild(sep);
        return;
      }

      const digitEl = document.createElement("span");
      digitEl.className = "digit";

      const trackEl = document.createElement("span");
      trackEl.className = "track";

      const finalDigit = Number(ch);
      const steps = loops * 10 + finalDigit;

      const totalSteps = buildDigitTrack(trackEl, steps, leadCycles);

      digitEl.appendChild(trackEl);
      el.appendChild(digitEl);

      const fromTop = digitIndex % 2 === 0; // even: from top

      // We want:
      // - fromTop: start somewhere NOT final, then move DOWN to finalDigit
      // - fromBottom: start at 0, then move UP to finalDigit (at the end of the track)
      //
      // Visible digit at y = -k em is track[k].
      // So to finish on finalDigit:
      // - fromTop should END at y = -finalDigit (so track[finalDigit] is visible)
      // - fromBottom should END at y = -totalSteps (last item is finalDigit)

      const endY = fromTop ? `${-finalDigit}em` : `${-totalSteps}em`;

      // Pick a start index that is safely above the final digit and not equal to it.
      // Offset by 7 so the first visible digit differs (and feels like it "comes from top").
      const startOffset = 7;
      const startIndex = fromTop
        ? Math.max(finalDigit + 1, totalSteps - (leadCycles * 10 + startOffset))
        : 0;

      const startY = `${-startIndex}em`;

      gsap.set(trackEl, { y: startY });

      digits.push({
        trackEl,
        totalSteps,
        finalDigit,
        idx: digitIndex,
        fromTop,
        startY,
        endY,
      });

      digitIndex += 1;
    });

    const tl = gsap.timeline({ paused: true });
    const totalDigits = digits.length;

    digits.forEach((dObj) => {
      const t = totalDigits <= 1 ? 0 : dObj.idx / (totalDigits - 1);
      const d = duration + t * staggerExtra;

      tl.to(
        dObj.trackEl,
        {
          y: dObj.endY,
          duration: d,
          ease,
        },
        0
      );
    });

    whenMeasurable(el, () => {
      // Kill old triggers attached to this element (SPA-safe)
      ScrollTrigger.getAll().forEach((st) => {
        if (st.trigger === el) st.kill();
      });

      ScrollTrigger.create({
        trigger: el,
        start,
        once,
        invalidateOnRefresh: true,
        onEnter: () => tl.play(0),
      });

      ScrollTrigger.refresh(true);
    });
  });
}
